import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';

import { getCurrencyFormatter } from 'reducers/configs/settings';
import { useSize } from 'utils/hooks/UI';

const useStyles = makeStyles((theme: Theme) => ({
  tableContainer: {
    marginBottom: '8px',
    marginTop: '2px',
    // @ts-ignore
    background: theme.palette.POS?.main ?? '#f4c345',
    borderRadius: theme.shape.borderRadius,
    '& td, & th': {
      color: theme.palette.common.black,
      borderBottom: 'none',
      padding: '6px',
      '&:first-child': {
        paddingLeft: '12px',
      },
      '&:last-child': {
        padding: 0,
      },
    },
    '& th': {
      paddingBottom: 0,
      fontWeight: 700,
      lineHeight: '1.2rem',
    },
    '& .MuiGrid-item:first-child': {
      fontWeight: 700,
    },
    '& .MuiGrid-item:last-child': {
      textAlign: 'end',
    },
    '& .MuiIconButton-root': {
      color: theme.palette.common.black,
    },
  },
}));

interface Props {
  vat: number;
  net: number;
  sum: number;
  rounding: number;
  discount: number;
  quantity: number;
  extraTaxRows: { taxName: string; taxAmount: number }[];
  showQuantity: boolean;
}

const HORIZONTAL_MARGIN = 30;
const TABLE_COLUMN_MAX_WIDTH = 160;
const STATIC_COLUMNS = 3;
const DYNAMIC_COLUMNS = 3;

function TableTotalMobile({
  vat,
  net,
  sum,
  rounding,
  discount,
  quantity,
  extraTaxRows,
  showQuantity,
}: Props) {
  const { t } = useTranslation('billTable');
  const styles = useStyles();

  const format = useSelector(getCurrencyFormatter);

  const [open, setOpen] = useState(false);

  const { width } = useSize(200);
  const tableColumnAmount = Math.floor(
    (width - HORIZONTAL_MARGIN) / TABLE_COLUMN_MAX_WIDTH,
  );
  const clampedTableColumnAmount = Math.max(
    0,
    Math.min(tableColumnAmount - STATIC_COLUMNS, DYNAMIC_COLUMNS),
  );

  const extraData = ([
    {
      show: discount > 0,
      name: t('footers.discount'),
      value: format(discount, { roundDown: true }),
    },
    {
      show: rounding > 0,
      name: t('footers.rounding'),
      value: format(rounding),
    },
    {
      show: !!showQuantity,
      name: t('footers.amount'),
      value: quantity,
    },
  ] as const).filter(data => data.show);

  const extraColumns = extraData.slice(0, clampedTableColumnAmount);
  const collapsibleSectionData = extraData.slice(clampedTableColumnAmount);

  const collapsibleSectionHasContent =
    collapsibleSectionData.length > 0 || extraTaxRows.length > 0;

  return (
    <div data-testid="table-total" className={styles.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell> {t('footers.vat')}</TableCell>
            <TableCell>{t('footers.net')}</TableCell>
            <TableCell>{t('footers.total')}</TableCell>
            {extraColumns.map(data => (
              <TableCell key={data.name}>{data.name}</TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>{format(vat)}</TableCell>
            <TableCell>{format(net)}</TableCell>
            <TableCell> {format(sum)}</TableCell>
            {extraColumns.map(data => (
              <TableCell key={data.name}>{data.value}</TableCell>
            ))}
            <TableCell style={{ padding: 0 }}>
              {collapsibleSectionHasContent ? (
                <IconButton size="small" onClick={() => setOpen(prev => !prev)}>
                  {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              ) : null}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
              <Collapse
                in={open && collapsibleSectionHasContent}
                timeout="auto"
                unmountOnExit
              >
                <Box marginX={4} marginY={1}>
                  {collapsibleSectionData.map(data => (
                    <Grid
                      key={data.name}
                      container
                      data-testid="bill-discount-total"
                    >
                      <Grid item xs={6} data-testid="bill-discount-total-label">
                        {data.name}
                      </Grid>
                      <Grid item xs={6} data-testid="bill-discount-total-sum">
                        {data.value}
                      </Grid>
                    </Grid>
                  ))}
                  {extraTaxRows.length > 0 &&
                    extraTaxRows.map(row => (
                      <Grid
                        key={row.taxName}
                        container
                        data-testid="bill-tax-total"
                        data-test-key={row.taxName}
                      >
                        <Grid
                          item
                          xs={6}
                          data-testid="bill-tax-total-label"
                          data-test-key={row.taxName}
                        >
                          {t(row.taxName)}
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          data-testid="bill-tax-total-sum"
                          data-test-key={row.taxName}
                        >
                          {format(row.taxAmount)}
                        </Grid>
                      </Grid>
                    ))}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

export default TableTotalMobile;
